import type { ReactNode } from 'react';
import { useThemeStore, type LabelPlacement } from '../../stores/themeStore';

export type { LabelPlacement };

/**
 * The placement a field uses: its own `labelPlacement` prop when given,
 * otherwise the app-wide setting (User Settings > Appearance > Field labels,
 * kept in the theme store; default 'border').
 */
export function useLabelPlacement(prop?: LabelPlacement): LabelPlacement {
  const app: unknown = useThemeStore((s) => s.labelPlacement);
  if (prop) return prop;
  // Checked here rather than with themeStore's isLabelPlacement: test
  // files that mock the store with a bare useThemeStore (no labelPlacement,
  // no other exports) then still get the default instead of a crash.
  return app === 'above' || app === 'left' ? app : 'border';
}

/**
 * The label a control draws on its own outline (MUI TextField `label`), or
 * undefined when the label is not on the border. Pass it as the TextField's
 * `label`, with `InputLabelProps={{ shrink: true }}` when it is set, so the
 * label always sits in the notch and never covers the value or a placeholder.
 */
export function borderLabel(placement: LabelPlacement, label?: string, required?: boolean): ReactNode {
  if (placement !== 'border' || !label) return undefined;
  return (
    <>
      {label}
      {required && <span className="app-field-required" aria-hidden="true">*</span>}
    </>
  );
}
